'use client';

import Avatar from '@/components/next-ui/Avatar';
import AppLink from '@/components/next-ui/Link/Link';
import { NEKI_LOGO_S3_URL } from '@/constants/neki.constant';
import OrgType from '@/constants/orgType.constant';
import { CommunityRoles } from '@/utils/communityRoles';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { formatNumberForLocale, getAdminLoginUrl } from '@/utils/stringHelpers';
import Typography from '@ui/Typography';
import { cx } from 'class-variance-authority';
import { kebabCase, last } from 'lodash-es';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

function MyCommunity(props) {
  const { community: userActiveCommunity, user, accessToken } = props;
  const isCommunityAdmin = userActiveCommunity?.role === CommunityRoles.Admin;
  const isNonProfitAdmin =
    isCommunityAdmin && userActiveCommunity?.orgType === OrgType.NonProfit;
  const hasPublicProfile = userActiveCommunity?.publicProfile?.enabled;

  const isCompanyCommunity = [OrgType.Business, OrgType.ChangeMakers].includes(
    userActiveCommunity?.orgType,
  );
  const isNonProfitCommunity =
    userActiveCommunity?.orgType === OrgType.NonProfit;

  const hasPublicCompanyProfile = hasPublicProfile && isCompanyCommunity;
  const showHomeAnalytics =
    isCommunityAdmin && (isNonProfitCommunity || hasPublicCompanyProfile);

  const primaryCommunity = userActiveCommunity?.primaryCommunity;

  const nekiAdminLoginUrl = getAdminLoginUrl(user?.email, accessToken);

  const getNonProfitLink = () => {
    if (isNonProfitAdmin) {
      let slug = hasPublicProfile
        ? last(userActiveCommunity.publicProfile.slug?.split('/'))
        : null;

      if (!slug) {
        slug = `${kebabCase(userActiveCommunity?.name)}-${
          userActiveCommunity?.nonProfit?.id
        }`;
      }
      return `/organization/${slug}`;
    }
    return null;
  };

  const getCommunityLogoUrl = (community) => {
    if (community.logoUrl) {
      return getQualifiedImageUrl(community.logoUrl);
    }
    return NEKI_LOGO_S3_URL;
  };

  return (
    <div className="relative flex flex-col">
      {primaryCommunity && (
        <div className="relative">
          <div className="flex items-center gap-2">
            <Avatar
              src={getCommunityLogoUrl(primaryCommunity)}
              alt={primaryCommunity.name}
              profile="company"
              className="h-10 w-10"
            />
            <Typography
              title={primaryCommunity.name}
              variant="h3"
              component="span"
              className="truncate text-left">
              {primaryCommunity.name && primaryCommunity.name.length > 16
                ? `${primaryCommunity.name.slice(0, 16)}...`
                : primaryCommunity.name}
            </Typography>
          </div>

          <div className="absolute left-5 top-[64px] h-px w-6 bg-gray-500" />
          <div className="absolute left-5 top-10 h-6 w-px bg-gray-500" />
        </div>
      )}

      {userActiveCommunity && (
        <div className={cx(primaryCommunity && 'ml-12 mt-2')}>
          <div className="flex items-center gap-2">
            <Avatar
              src={getCommunityLogoUrl(userActiveCommunity)}
              alt={userActiveCommunity.name}
              profile="company"
              className="h-10 w-10"
            />
            <Typography
              title={userActiveCommunity.name}
              variant="h3"
              component="span"
              className="truncate text-left">
              {userActiveCommunity.name && userActiveCommunity.name.length > 16
                ? `${userActiveCommunity.name.slice(0, 16)}...`
                : userActiveCommunity.name}
            </Typography>
          </div>

          {isCommunityAdmin && (
            <AppLink href={'https://help.neki.io/en/article/what-is-neki-score'} target="__blank" passHref>
              <div className="mt-2 flex flex-col gap-2">
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <Image
                      src="/assets/images/neki-score.png"
                      alt="neki-score"
                      width={48}
                      height={48}
                      className="rounded-full"
                    />

                    <div className="flex flex-col">
                      <div className="flex items-center gap-1">
                        <Typography className="text-16 font-medium">
                          Neki Score:{' '}
                          {formatNumberForLocale(user?.activeCommunity?.points)}
                        </Typography>
                      </div>

                      <Typography className="text-11 italic text-gray-500 sm:text-12">
                        Measure of a community’s collective impact on Neki
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </AppLink>
          )}

          <div className="ml-2 mt-4 flex flex-col items-start gap-2">
            {hasPublicProfile && (
              <Link href={`/${userActiveCommunity.publicProfile.slug}`}>
                <Typography
                  variant="subtitle1"
                  className="hover:text-text-hyperlink">
                  Public Page
                </Typography>
              </Link>
            )}
            {isNonProfitAdmin && (
              <Link href={getNonProfitLink()}>
                <Typography
                  variant="subtitle1"
                  className="hover:text-text-hyperlink">
                  My Non Profit
                </Typography>
              </Link>
            )}
            {isCommunityAdmin && (
              <Typography
                component="a"
                href={nekiAdminLoginUrl}
                variant="subtitle1"
                className="hover:text-text-hyperlink">
                Administration
              </Typography>
            )}
            {showHomeAnalytics && (
              <Link href={`/analytics`}>
                <Typography
                  variant="subtitle1"
                  className="hover:text-text-hyperlink">
                  Analytics
                </Typography>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(MyCommunity);
